/**
 * Витринная страница Q&A/документов (design §6). Один payload на сессию (все локали на руках —
 * FE выбирает язык и ищет по подстроке, §5.4/§10). Байты PDF/картинок отдаются JWT-эндпоинтами —
 * `<img>`/`<a>` не умеют слать Bearer, поэтому тянем их axios'ом как blob и подменяем src.
 */
import { ref } from 'vue';
import { http } from './http';
import { createBlobUrlCache } from '@/utils/blob';

export type LocaleMap = Record<string, string>;

export interface FaqEntry { id: string; question: LocaleMap; answer: LocaleMap; }
/** {@code id===null} — псевдо-категория «Прочее» (записи без категории). */
export interface FaqCategory { id: string | null; name: LocaleMap | null; entries: FaqEntry[]; }
export interface DocumentItem {
  id: string;
  kind: 'PDF' | 'TEXT';
  title: LocaleMap;
  description: LocaleMap | null;
  textBody: LocaleMap | null;   // kind=TEXT: markdown-карта
  locales: string[] | null;     // kind=PDF: доступные языки файлов
}
export interface DocumentGroup { setName: string; documents: DocumentItem[]; }
export interface FaqPage {
  faqCategories: FaqCategory[];
  docsAvailable: boolean;
  documentGroups: DocumentGroup[];
}

const page = ref<FaqPage | null>(null);
let inflight: Promise<void> | null = null;

/** Реактивный payload (null пока не загружен). */
export function faqData() {
  return page;
}

/** Идемпотентно грузит страницу (один сетевой запрос на сессию). */
export function ensureFaqLoaded(): Promise<void> {
  if (page.value) return Promise.resolve();
  inflight ??= http
    .get<FaqPage>('/v1/showcase/me/faq')
    .then(r => { page.value = r.data; })
    .catch(e => { inflight = null; throw e; });
  return inflight;
}

/** Сброс + перезагрузка (напр. после логина другим клиентом). */
export function reloadFaq(): Promise<void> {
  page.value = null;
  inflight = null;
  clearFaqBlobs();
  return ensureFaqLoaded();
}

// ── локале-пикеры (design §10) ────────────────────────────────────────────────

const FALLBACK_ORDER = ['en', 'ru', 'zh'];

function blankToNull(v: string | undefined | null): string | null {
  return v && v.trim() ? v : null;
}

/** Q&A/категории: язык клиента или null (нет перевода → запись скрывается, без en-fallback). */
export function pickStrict(map: LocaleMap | null | undefined, locale: string): string | null {
  if (!map) return null;
  return blankToNull(map[locale]);
}

/** Документы: язык клиента, иначе любой доступный (показываем безусловно). */
export function pick(map: LocaleMap | null | undefined, locale: string): string | null {
  if (!map) return null;
  const exact = blankToNull(map[locale]);
  if (exact) return exact;
  for (const k of FALLBACK_ORDER) {
    const c = blankToNull(map[k]);
    if (c) return c;
  }
  for (const k of Object.keys(map).sort()) {
    const c = blankToNull(map[k]);
    if (c) return c;
  }
  return null;
}

// ── blob-эндпоинты (JWT — через axios, не через src/href) ─────────────────────

// Keyed by request path (each path uniquely encodes the resource + locale).
const blobs = createBlobUrlCache(path => http.get(path, { responseType: 'blob' }).then(r => r.data as Blob));

/** Object-URL картинки Q&A по id (кэш на сессию). Для подмены `data-faq-img` → `src`. */
export function faqImageUrl(id: string): Promise<string> {
  return blobs.get(`/v1/showcase/me/faq/images/${id}`);
}

/** Тянет PDF документа (язык клиента, иначе бэк отдаёт любой) и открывает в новой вкладке. */
export async function openFaqPdf(templateId: string, locale?: string): Promise<void> {
  const query = locale ? `?locale=${encodeURIComponent(locale)}` : '';
  const url = await blobs.get(`/v1/showcase/me/documents/${templateId}/file${query}`);
  window.open(url, '_blank', 'noopener');
}

/**
 * Переносит `src` наших картинок в `data-faq-img` (id), чтобы браузер НЕ дёргал JWT-эндпоинт без
 * Bearer. Реальный src подставит компонент по требованию (когда вопрос раскрыт).
 */
export function deferFaqImages(html: string): string {
  return html.replace(
    /<img src="\/api\/v1\/showcase\/me\/faq\/images\/([^"]+)"/g,
    '<img data-faq-img="$1"',
  );
}

/** Освобождает object-URL'ы (при размонтировании/смене клиента). */
export function clearFaqBlobs(): void {
  blobs.clearAll();
}
